/** 活动取消报名截止规则（与后端 CancelDeadlineMode 一致） */
export type ActivityCancelDeadlineMode = 'reg_end' | 'event_start' | 'custom'

export type ActivityCancelRegistrationFields = {
  allowCancelWithRefund?: boolean
  cancelDeadlineMode?: string | null
  cancelDeadlineAt?: string | null
  cancelDeadlineResolvedAt?: string | null
  regEndDate?: string | null
  date?: string | null
}

const MODE_REG_END: ActivityCancelDeadlineMode = 'reg_end'
const MODE_EVENT_START: ActivityCancelDeadlineMode = 'event_start'
const MODE_CUSTOM: ActivityCancelDeadlineMode = 'custom'

export function normalizeCancelDeadlineMode(mode?: string | null): ActivityCancelDeadlineMode {
  const m = String(mode ?? '').trim().toLowerCase()
  if (m === MODE_REG_END) return MODE_REG_END
  if (m === MODE_CUSTOM) return MODE_CUSTOM
  return MODE_EVENT_START
}

/** 报名截止上界：仅日期则至当日 23:59:59.999（face UTC 字符串比较，与后端一致） */
function regEndUpperBoundIso(regEndDate: string): string {
  const raw = regEndDate.trim()
  if (!raw) return ''
  const hasTime = raw.includes('T') || /\d:\d/.test(raw)
  if (hasTime) return raw
  const d = raw.slice(0, 10)
  return `${d}T23:59:59.999Z`
}

export function resolveActivityCancelDeadlineIso(activity: ActivityCancelRegistrationFields): string {
  const mode = normalizeCancelDeadlineMode(activity.cancelDeadlineMode)
  if (mode === MODE_CUSTOM) {
    return String(activity.cancelDeadlineAt ?? '').trim()
  }
  if (mode === MODE_REG_END) {
    const regEnd = String(activity.regEndDate ?? '').trim()
    if (regEnd) return regEndUpperBoundIso(regEnd)
    return String(activity.date ?? '').trim()
  }
  return String(activity.date ?? '').trim()
}

export function isPastActivityCancelDeadline(
  activity: ActivityCancelRegistrationFields,
  nowMs: number = Date.now(),
): boolean {
  const resolved =
    String(activity.cancelDeadlineResolvedAt ?? '').trim() ||
    resolveActivityCancelDeadlineIso(activity)
  if (!resolved) return false
  const t = Date.parse(resolved)
  if (!Number.isFinite(t)) return false
  return nowMs > t
}

export function canMemberCancelRegistration(activity: ActivityCancelRegistrationFields): boolean {
  if (!activity.allowCancelWithRefund) return false
  return !isPastActivityCancelDeadline(activity)
}

export function activityMayChargeOnRegister(activity: {
  mayChargeOnRegister?: boolean | null
  expenseType?: string | null
  onlinePayAmountFen?: number | null
  registrationOptions?: {
    enabled?: boolean
    items?: Array<{
      choices?: Array<{ chargeEnabled?: boolean; amountFen?: number }>
    }>
  } | null
}): boolean {
  if (typeof activity.mayChargeOnRegister === 'boolean') return activity.mayChargeOnRegister
  const et = String(activity.expenseType ?? '').trim()
  if (et === '在线支付' && Number(activity.onlinePayAmountFen ?? 0) > 0) return true
  const cfg = activity.registrationOptions
  if (!cfg?.enabled || !cfg.items?.length) return false
  return cfg.items.some((q) =>
    (q.choices ?? []).some((c) => c.chargeEnabled && Number(c.amountFen ?? 0) > 0),
  )
}

export const CANCEL_DEADLINE_MODE_OPTIONS: { value: ActivityCancelDeadlineMode; label: string }[] = [
  { value: 'reg_end', label: '报名截止时间' },
  { value: 'event_start', label: '活动开始时间' },
  { value: 'custom', label: '自定义时间' },
]

export function formatActivityCancelDeadlineHint(activity: ActivityCancelRegistrationFields): string {
  const mode = normalizeCancelDeadlineMode(activity.cancelDeadlineMode)
  if (mode === 'reg_end') return '报名截止时间前可取消（未设报名截止则按活动开始）'
  if (mode === 'event_start') return '活动开始时间前可取消'
  const at = String(activity.cancelDeadlineAt ?? activity.cancelDeadlineResolvedAt ?? '').trim()
  if (at) return `自定义截止时刻前可取消`
  return '截止时刻前可取消'
}
